import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { CheckCircle2, Clock, DollarSign, XCircle } from 'lucide-react';
import type { Recovery } from '../../shared/types';
import { apiFetch } from '../lib/api';

export default function Recoveries() {
  const [recoveries, setRecoveries] = useState<Recovery[]>([]);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRecoveries = useCallback(async () => {
    try {
      const data = await apiFetch<Recovery[]>('/api/recoveries');
      setRecoveries(data);
      setError(null);
    } catch (fetchError) {
      console.error('Failed to fetch recoveries:', fetchError);
      setError('Failed to load recoveries.');
    }
  }, []);

  useEffect(() => {
    void fetchRecoveries();
  }, [fetchRecoveries]);

  const decideRecovery = async (recoveryId: number, decision: 'approve' | 'reject') => {
    setActionId(recoveryId);
    setError(null);
    try {
      await apiFetch<Recovery>(`/api/recoveries/${recoveryId}/${decision}`, {
        method: 'POST',
        credentials: 'include',
      });
      await fetchRecoveries();
    } catch (actionError) {
      console.error(`Failed to ${decision} recovery:`, actionError);
      setError(`Failed to ${decision} recovery.`);
    } finally {
      setActionId(null);
    }
  };

  const totalRecoveredCents = recoveries
    .filter((recovery) => recovery.status === 'COMPLETED')
    .reduce((sum, recovery) => sum + recovery.amount_cents, 0);

  const pendingAmountCents = recoveries
    .filter((recovery) => recovery.approval_status === 'PENDING')
    .reduce((sum, recovery) => sum + recovery.amount_cents, 0);

  const activeCases = recoveries.filter(
    (recovery) => !['COMPLETED', 'REJECTED', 'CANCELLED'].includes(recovery.status)
  ).length;

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Revenue Recoveries</h1>
          <p className="text-slate-400">Review and track recovery recommendations</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg p-6">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mb-4" />
            <p className="text-sm text-slate-400 mb-1">Total Recovered</p>
            <p className="text-3xl font-bold text-white">
              ${(totalRecoveredCents / 100).toFixed(2)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-6">
            <Clock className="w-5 h-5 text-cyan-400 mb-4" />
            <p className="text-sm text-slate-400 mb-1">Pending Approval</p>
            <p className="text-3xl font-bold text-white">
              ${(pendingAmountCents / 100).toFixed(2)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-6">
            <DollarSign className="w-5 h-5 text-purple-400 mb-4" />
            <p className="text-sm text-slate-400 mb-1">Active Cases</p>
            <p className="text-3xl font-bold text-white">{activeCases}</p>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-slate-900/50 border-b border-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Amount</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Priority</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Method</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Approval</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Created</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recoveries.length > 0 ? (
                recoveries.map((recovery) => (
                  <tr key={recovery.id} className="border-b border-slate-700/30">
                    <td className="p-4 text-sm font-medium text-white">#{recovery.id}</td>
                    <td className="p-4 text-sm text-white">
                      {recovery.currency} ${(recovery.amount_cents / 100).toFixed(2)}
                    </td>
                    <td className="p-4 text-sm text-slate-300">{recovery.priority}</td>
                    <td className="p-4 text-sm text-slate-300">
                      {recovery.suggested_method.replaceAll('_', ' ')}
                    </td>
                    <td className="p-4 text-sm text-slate-300">{recovery.status}</td>
                    <td className="p-4 text-sm text-slate-300">{recovery.approval_status}</td>
                    <td className="p-4 text-sm text-slate-300">
                      {new Date(recovery.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      {recovery.approval_status === 'PENDING' ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={actionId === recovery.id}
                            onClick={() => void decideRecovery(recovery.id, 'approve')}
                            className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={actionId === recovery.id}
                            onClick={() => void decideRecovery(recovery.id, 'reject')}
                            className="inline-flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Decided</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-500">
                    No recovery recommendations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
